// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
  PaginationInfoSchema,
  ResponseMessageSchema,
  StatusSchema,
} from "./utilitySchemas";

export const ViewingsDispatchLifecycleStatusSchema = z.enum([
  "pending",
  "suggested",
  "noMatch",
  "contacted",
  "accepted",
  "scheduled",
  "completed",
  "completedRemotely",
  "onHold",
  "declined",
  "needsAction",
  "cancelled",
]);
export type ViewingsDispatchLifecycleStatus = z.infer<
  typeof ViewingsDispatchLifecycleStatusSchema
>;

export const ViewingsDispatchFlagSchema = z.object({
  noMatch: z.boolean(),
  waitingOver48h: z.boolean(),
  acceptedAfterDeadline: z.boolean(),
  reassignedInvalidation: z.boolean(),
});
export type ViewingsDispatchFlags = z.infer<typeof ViewingsDispatchFlagSchema>;

export const ViewingsDispatchErrorCodeSchema = z.enum([
  "VIEWINGS_DISPATCH_DISABLED",
  "VIEWINGS_DISPATCH_STALE_VERSION",
  "VIEWINGS_DISPATCH_OWNERSHIP_FAILURE",
  "VIEWINGS_DISPATCH_INVALID_TRANSITION",
  "VIEWINGS_DISPATCH_MISSING_ACTIVE_ATTEMPT",
  "VIEWINGS_DISPATCH_NOT_FOUND",
  "VIEWINGS_DISPATCH_VALIDATION_FAILED",
  "INSPECTOR_EVIDENCE_INVALID_METADATA",
  "INSPECTOR_EVIDENCE_EXACT_JPEG_REQUIRED",
  "INSPECTOR_EVIDENCE_INVALID_JPEG",
  "INSPECTOR_EVIDENCE_HASH_MISMATCH",
  "INSPECTOR_EVIDENCE_METADATA_MISMATCH",
  "INSPECTOR_EVIDENCE_ATTEMPT_INACTIVE",
  "INSPECTOR_EVIDENCE_ID_CONFLICT",
  "INSPECTOR_EVIDENCE_FILE_MISSING",
  "INSPECTOR_COMPLETION_EVIDENCE_REQUIRED",
  "INSPECTOR_COMPLETION_COMPLETED_ELSEWHERE",
  "INSPECTOR_COMPLETION_ID_CONFLICT",
]);
export type ViewingsDispatchErrorCode = z.infer<
  typeof ViewingsDispatchErrorCodeSchema
>;

export const ViewingsDispatchActionReasonSchema = z.enum([
  "missingCoordinates",
  "inspectorDeclined",
  "noResponse48h",
  "invalidDateReply",
  "noMatch",
  "outreachFailed",
  "reassigned",
  "manualReview",
  "viewingNoLongerRequired",
]);
export type ViewingsDispatchActionReason = z.infer<
  typeof ViewingsDispatchActionReasonSchema
>;

export const ViewingsDispatchSourceTypeSchema = z.enum([
  "request",
  "tempRequest",
]);
export type ViewingsDispatchSourceType = z.infer<
  typeof ViewingsDispatchSourceTypeSchema
>;

export const ViewingsDispatchCustomerAttributionTypeSchema = z.enum([
  "external",
  "internal",
]);
export type ViewingsDispatchCustomerAttributionType = z.infer<
  typeof ViewingsDispatchCustomerAttributionTypeSchema
>;

export const ViewingsDispatchInspectorSummarySchema = z.object({
  id: z.string().min(1),
  fullName: z.string().min(1),
  phone: z.string().min(1),
});
export type ViewingsDispatchInspectorSummary = z.infer<
  typeof ViewingsDispatchInspectorSummarySchema
>;

export const ViewingsDispatchAttemptSummarySchema = z.object({
  id: z.string().min(1),
  status: z.string().min(1),
  inspectorId: z.string().min(1),
  inspectorName: z.string().min(1),
  contactedAt: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  declinedAt: z.string().nullable(),
});
export type ViewingsDispatchAttemptSummary = z.infer<
  typeof ViewingsDispatchAttemptSummarySchema
>;

export const ViewingsDispatchSummarySchema = z.object({
  id: z.string().min(1),
  version: z.number().int().positive(),
  lifecycleStatus: ViewingsDispatchLifecycleStatusSchema,
  actionReason: ViewingsDispatchActionReasonSchema.nullable(),
  sourceType: ViewingsDispatchSourceTypeSchema,
  requestId: z.string().min(1),
  evalionId: z.string().min(1),
  customerLabel: z.string().min(1),
  customerAttributionType: ViewingsDispatchCustomerAttributionTypeSchema,
  customerAttributionSource: z.string().min(1).optional(),
  requestType: z.string().min(1),
  deadlineDate: z.string().nullable(),
  orderedAt: z.string().nullable(),
  viewingDeadline: z.string().nullable().optional(),
  offerLink: z.string().min(1).nullable().optional(),
  addressLine: z.string().min(1),
  formattedAddress: z.string().nullable(),
  hasCoordinates: z.boolean(),
  viewingTypes: z.array(z.string().min(1)),
  assignedDispatcherId: z.number().int().nullable(),
  assignedDispatcherName: z.string().nullable(),
  suggestedInspector: ViewingsDispatchInspectorSummarySchema.nullable(),
  assignedInspector: ViewingsDispatchInspectorSummarySchema.nullable(),
  scheduledFor: z.string().nullable(),
  completedAt: z.string().nullable().optional(),
  inspectorInstructions: z.string().nullable().optional(),
  latestAttempt: ViewingsDispatchAttemptSummarySchema.nullable(),
  outreachFailureReason: z.string().nullable().optional(),
  flags: ViewingsDispatchFlagSchema,
  updatedAt: z.string().min(1),
});
export type ViewingsDispatchSummary = z.infer<
  typeof ViewingsDispatchSummarySchema
>;

export const ViewingsDispatchUpdateRequestSchema = z
  .object({
    version: z.number().int().positive(),
    lifecycleStatus: ViewingsDispatchLifecycleStatusSchema.optional(),
    viewingDeadline: z
      .string()
      .datetime({ offset: true })
      .nullable()
      .optional(),
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    completedAt: z.string().datetime({ offset: true }).nullable().optional(),
    inspectorInstructions: z.string().trim().max(4000).nullable().optional(),
  })
  .strict()
  .refine(
    ({
      lifecycleStatus,
      viewingDeadline,
      scheduledFor,
      completedAt,
      inspectorInstructions,
    }) =>
      lifecycleStatus !== undefined ||
      viewingDeadline !== undefined ||
      scheduledFor !== undefined ||
      completedAt !== undefined ||
      inspectorInstructions !== undefined,
    { message: "At least one viewing management field is required" },
  );
export type ViewingsDispatchUpdateRequest = z.infer<
  typeof ViewingsDispatchUpdateRequestSchema
>;

export const ViewingsDispatchQueueResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    dispatches: z.array(ViewingsDispatchSummarySchema),
    paginationInfo: PaginationInfoSchema,
  }),
});
export type ViewingsDispatchQueueResponse = z.infer<
  typeof ViewingsDispatchQueueResponseSchema
>;

export const ViewingsDispatchPropertySnapshotSchema = z.object({
  addressLine: z.string().min(1),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  formattedAddress: z.string().nullable(),
  coordinatesVerifiedAt: z.string().nullable(),
  hasCoordinates: z.boolean(),
});
export type ViewingsDispatchPropertySnapshot = z.infer<
  typeof ViewingsDispatchPropertySnapshotSchema
>;

export const ViewingsDispatchMatchReasonSchema = z.enum([
  "available",
  "blocked_portal_access",
  "capability_match",
  "missing_coordinates",
  "outside_radius",
  "product_match",
  "unsupported_capability",
  "unsupported_product",
  "vacation_unavailable",
  "vacation_window_incomplete",
  "viewing_requirement_unresolved",
  "within_radius",
]);
export type ViewingsDispatchMatchReason = z.infer<
  typeof ViewingsDispatchMatchReasonSchema
>;

export const ViewingsDispatchNoMatchSummarySchema = z.record(
  ViewingsDispatchMatchReasonSchema,
  z.number().int().nonnegative(),
);
export type ViewingsDispatchNoMatchSummary = z.infer<
  typeof ViewingsDispatchNoMatchSummarySchema
>;

export const ViewingsDispatchMatchRunCandidateSchema = z.object({
  id: z.string().min(1),
  rank: z.number().int().positive(),
  inspectorId: z.string().min(1),
  inspector: ViewingsDispatchInspectorSummarySchema,
  distanceKm: z.number().nonnegative(),
  isWithinRadius: z.boolean(),
  matchReasons: z.array(ViewingsDispatchMatchReasonSchema),
});
export type ViewingsDispatchMatchRunCandidate = z.infer<
  typeof ViewingsDispatchMatchRunCandidateSchema
>;

export const ViewingsDispatchMatchRunSchema = z.object({
  id: z.string().min(1),
  algorithmVersion: z.string().min(1),
  createdAt: z.string().min(1),
  propertyLatitudeSnapshot: z.number(),
  propertyLongitudeSnapshot: z.number(),
  candidates: z.array(ViewingsDispatchMatchRunCandidateSchema),
  noMatchSummary: ViewingsDispatchNoMatchSummarySchema.optional(),
});
export type ViewingsDispatchMatchRun = z.infer<
  typeof ViewingsDispatchMatchRunSchema
>;

export const ViewingsDispatchDetailResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    dispatch: ViewingsDispatchSummarySchema,
    property: ViewingsDispatchPropertySnapshotSchema,
    matchRuns: z.array(ViewingsDispatchMatchRunSchema),
    attempts: z.array(z.record(z.unknown())),
    communications: z.array(z.record(z.unknown())),
    events: z.array(z.record(z.unknown())),
    inspectorUploads: z.array(z.record(z.unknown())),
  }),
});
export type ViewingsDispatchDetailResponse = z.infer<
  typeof ViewingsDispatchDetailResponseSchema
>;

export const ViewingsDispatchTimelineEntrySchema = z.object({
  id: z.string().min(1),
  at: z.string().min(1),
  actorName: z.string().min(1),
  action: z.string().min(1),
});
export type ViewingsDispatchTimelineEntry = z.infer<
  typeof ViewingsDispatchTimelineEntrySchema
>;

export const InspectorPortalInspectionTypeSchema = z.enum([
  "interior",
  "exterior",
  "both",
]);
export type InspectorPortalInspectionType = z.infer<
  typeof InspectorPortalInspectionTypeSchema
>;

export const InspectorPortalDateReplyStatusSchema = z.enum([
  "not_requested",
  "awaiting_reply",
  "valid",
  "invalid_reprompt_sent",
  "manual_follow_up_required",
]);
export type InspectorPortalDateReplyStatus = z.infer<
  typeof InspectorPortalDateReplyStatusSchema
>;

export const InspectorPortalDispatcherNotificationStatusSchema = z.enum([
  "not_required",
  "notified_acceptance",
  "notified_decline",
  "notified_no_response",
  "notified_invalid_response",
]);
export type InspectorPortalDispatcherNotificationStatus = z.infer<
  typeof InspectorPortalDispatcherNotificationStatusSchema
>;

export const InspectorPortalAuditChannelSchema = z.enum([
  "sms_outbound",
  "sms_inbound",
  "portal",
  "system",
  "dispatcher_notification",
]);
export type InspectorPortalAuditChannel = z.infer<
  typeof InspectorPortalAuditChannelSchema
>;

export const InspectorPortalAuditEntrySchema = z.object({
  id: z.string().min(1),
  at: z.string().min(1),
  actorName: z.string().min(1),
  channel: InspectorPortalAuditChannelSchema,
  summary: z.string().min(1),
});
export type InspectorPortalAuditEntry = z.infer<
  typeof InspectorPortalAuditEntrySchema
>;

export const InspectorPortalOutreachSchema = z.object({
  dispatcher: z.object({
    name: z.string().min(1),
    phone: z.string().min(1),
  }),
  portalLink: z.string().min(1),
  smsOfferPreview: z.string().min(1),
  dateReplyStatus: InspectorPortalDateReplyStatusSchema,
  invalidResponseCount: z.number().int().nonnegative(),
  dispatcherNotificationStatus:
    InspectorPortalDispatcherNotificationStatusSchema,
  isAssignmentInvalidated: z.boolean(),
  auditLog: z.array(InspectorPortalAuditEntrySchema),
});
export type InspectorPortalOutreach = z.infer<
  typeof InspectorPortalOutreachSchema
>;

export const ViewingsDispatchRequestSchema = z.object({
  id: z.string().min(1),
  evalionId: z.string().min(1),
  title: z.string().min(1),
  viewingTypes: z.array(z.string()).optional(),
  addressLine: z.string().min(1),
  ownerName: z.string().min(1),
  lifecycleStatus: ViewingsDispatchLifecycleStatusSchema,
  deadlineDate: z.string().nullable(),
  offeredDate: z.string().nullable(),
  acceptedDate: z.string().nullable(),
  assignedDispatcherId: z.string().nullable(),
  assignedDispatcherName: z.string().nullable(),
  assignedInspectorId: z.string().nullable(),
  assignedInspectorName: z.string().nullable(),
  lastContactedAt: z.string().nullable(),
  flags: ViewingsDispatchFlagSchema,
  activityTimeline: z.array(ViewingsDispatchTimelineEntrySchema),
  inspectionType: InspectorPortalInspectionTypeSchema.optional(),
  productType: z.string().min(1).optional(),
  distanceKm: z.number().nonnegative().optional(),
  specialNotes: z.string().optional(),
  outreach: InspectorPortalOutreachSchema.optional(),
});
export type ViewingsDispatchRequest = z.infer<
  typeof ViewingsDispatchRequestSchema
>;

export const ViewingsDispatchInspectorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  phone: z.string().min(1),
  isOnVacation: z.boolean(),
});
export type ViewingsDispatchInspector = z.infer<
  typeof ViewingsDispatchInspectorSchema
>;

function splitCommaSeparatedQueryValue(value: unknown) {
  if (typeof value === "string" && value.includes(",")) {
    return value.split(",").filter(Boolean);
  }

  return value;
}

export const ViewingsDispatchQueueQuerySchema = z
  .object({
    search: z.string().optional(),
    appraiser: z.string().min(1).optional(),
    lifecycleStatus: z.preprocess(
      splitCommaSeparatedQueryValue,
      z
        .union([
          ViewingsDispatchLifecycleStatusSchema,
          z.array(ViewingsDispatchLifecycleStatusSchema),
        ])
        .optional(),
    ),
    assignedInspectorId: z.preprocess(
      splitCommaSeparatedQueryValue,
      z.union([z.string().min(1), z.array(z.string().min(1))]).optional(),
    ),
    limit: z.coerce.number().int().positive().optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
    sortBy: z
      .enum([
        "evalionId",
        "addressLine",
        "lifecycleStatus",
        "deadlineDate",
        "assignedInspectorName",
      ])
      .optional(),
    sortOrder: z.enum(["asc", "desc"]).optional(),
  })
  .strict();
export type ViewingsDispatchQueueQuery = z.infer<
  typeof ViewingsDispatchQueueQuerySchema
>;

export const ViewingsDispatchMockQueueResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    requests: z.array(ViewingsDispatchRequestSchema),
    inspectors: z.array(ViewingsDispatchInspectorSchema),
  }),
});
export type ViewingsDispatchMockQueueResponse = z.infer<
  typeof ViewingsDispatchMockQueueResponseSchema
>;

export const InspectorPortalResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    requests: z.array(ViewingsDispatchRequestSchema),
    inspectors: z.array(ViewingsDispatchInspectorSchema),
  }),
});
export type InspectorPortalResponse = z.infer<
  typeof InspectorPortalResponseSchema
>;

export const InspectorPortalTokenSchema = z.object({
  inspectorId: z.string().min(1),
  tokenType: z.literal("inspectorPortal"),
});
export type InspectorPortalToken = z.infer<typeof InspectorPortalTokenSchema>;

export const InspectorPortalAuthRequestOtpSchema = z.object({
  phone: z.string().min(1),
});
export type InspectorPortalAuthRequestOtp = z.infer<
  typeof InspectorPortalAuthRequestOtpSchema
>;

export const InspectorPortalAuthVerifyOtpSchema = z.object({
  phone: z.string().min(1),
  otp: z.string().min(4),
});
export type InspectorPortalAuthVerifyOtp = z.infer<
  typeof InspectorPortalAuthVerifyOtpSchema
>;

export const InspectorPortalAuthVerifyOtpResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspectorId: z.string().min(1),
    fullName: z.string().min(1),
  }),
});
export type InspectorPortalAuthVerifyOtpResponse = z.infer<
  typeof InspectorPortalAuthVerifyOtpResponseSchema
>;

export const InspectorPortalAssignmentSummarySchema = z.object({
  id: z.string().min(1),
  version: z.number().int().positive(),
  attemptId: z.string().min(1),
  lifecycleStatus: ViewingsDispatchLifecycleStatusSchema,
  evalionId: z.string().min(1),
  customerLabel: z.string().min(1),
  addressLine: z.string().min(1),
  inspectionType: InspectorPortalInspectionTypeSchema,
  productType: z.string().min(1),
  deadlineDate: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  approximateDistanceKm: z.number().nonnegative().nullable(),
  inspectorInstructions: z.string().nullable(),
  dispatcherContact: z
    .object({
      name: z.string().min(1),
      phone: z.string().min(1),
    })
    .nullable(),
  uploadStatus: z.object({
    fileCount: z.number().int().nonnegative(),
    qualifyingFileCount: z.number().int().nonnegative(),
  }),
});
export type InspectorPortalAssignmentSummary = z.infer<
  typeof InspectorPortalAssignmentSummarySchema
>;

export const InspectorPhotoAppLifecycleStatusSchema = z.enum([
  "accepted",
  "scheduled",
]);

export const InspectorPortalVisibleLifecycleStatusSchema = z.enum([
  "contacted",
  "accepted",
  "scheduled",
  "completed",
  "cancelled",
]);

export const InspectorPortalAssignmentsQuerySchema = z
  .object({
    limit: z.coerce.number().int().positive().max(100).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
    sortBy: z.enum(["statusDate", "status", "date"]).default("statusDate"),
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
    lifecycleStatus: z
      .string()
      .min(1)
      .transform((value) => value.split(","))
      .pipe(z.array(InspectorPortalVisibleLifecycleStatusSchema).min(1))
      .optional(),
  })
  .strict();
export type InspectorPortalAssignmentsQuery = z.infer<
  typeof InspectorPortalAssignmentsQuerySchema
>;

export const InspectorPortalAssignmentsResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    paginationInfo: PaginationInfoSchema,
    assignments: z.array(InspectorPortalAssignmentSummarySchema),
  }),
});
export type InspectorPortalAssignmentsResponse = z.infer<
  typeof InspectorPortalAssignmentsResponseSchema
>;

export const InspectorPortalUploadedFileSchema = z.object({
  id: z.number(),
  originalName: z.string().min(1),
  fileName: z.string().min(1),
  filePath: z.string().min(1),
  mimeType: z.string().min(1),
  size: z.number().nonnegative(),
  systemFileTag: z.literal("inspectorUploads"),
  signedUrl: z.string().url(),
});
export type InspectorPortalUploadedFile = z.infer<
  typeof InspectorPortalUploadedFileSchema
>;

const InspectorPhotoAppPixelDimensionsSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

const InspectorPhotoAppLegacyLocationSchema = z
  .object({
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
    accuracyMeters: z.number().finite().nonnegative(),
    headingDegrees: z.number().finite().min(0).lt(360).nullable(),
  })
  .strict();

const InspectorPhotoAppObservedLocationSchema =
  InspectorPhotoAppLegacyLocationSchema.extend({
    kind: z.literal("observed"),
    observedAt: z.string().datetime({ offset: true }),
  }).strict();

const InspectorPhotoAppUnavailableLocationSchema = z
  .object({
    kind: z.literal("unavailable"),
    reason: z.enum([
      "not-observed",
      "permission-denied",
      "unsupported",
      "failed",
    ]),
  })
  .strict();

export const InspectorPhotoAppEvidenceUploadMetadataSchema = z
  .object({
    evidenceId: z.string().uuid(),
    dispatchId: z.string().min(1),
    inspectorId: z.string().min(1),
    assignmentAttemptId: z.string().min(1),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    byteLength: z.number().int().positive(),
    capture: z.object({
      capturedAt: z.string().datetime({ offset: true }),
      timezone: z.string().min(1),
      evalionId: z.string().min(1),
      address: z.string().min(1),
      slot: z.string().min(1).nullable(),
      location: z.union([
        InspectorPhotoAppObservedLocationSchema,
        InspectorPhotoAppUnavailableLocationSchema,
        InspectorPhotoAppLegacyLocationSchema,
      ]),
      attributionLines: z.array(z.string().min(1)).min(1),
      sceneDimensions: InspectorPhotoAppPixelDimensionsSchema,
      finalDimensions: InspectorPhotoAppPixelDimensionsSchema,
    }),
  })
  .strict();
export type InspectorPhotoAppEvidenceUploadMetadata = z.infer<
  typeof InspectorPhotoAppEvidenceUploadMetadataSchema
>;

export const InspectorPhotoAppEvidenceReceiptSchema = z
  .object({
    receiptId: z.string().min(1),
    dispatchId: z.string().min(1),
    assignmentAttemptId: z.string().min(1),
    evidenceId: z.string().uuid(),
    fileId: z.string().min(1),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    receivedAt: z.string().datetime({ offset: true }),
    systemFileTag: z.literal("inspectorUploads"),
    qualifiesForCompletion: z.literal(true),
  })
  .strict();
export type InspectorPhotoAppEvidenceReceipt = z.infer<
  typeof InspectorPhotoAppEvidenceReceiptSchema
>;

export const InspectorPhotoAppEvidenceUploadResponseSchema = z.object({
  receipt: InspectorPhotoAppEvidenceReceiptSchema,
});
export type InspectorPhotoAppEvidenceUploadResponse = z.infer<
  typeof InspectorPhotoAppEvidenceUploadResponseSchema
>;

export const InspectorPhotoAppCompletionRequestSchema = z
  .object({
    completionId: z.string().min(1),
    assignmentAttemptId: z.string().min(1),
  })
  .strict();
export type InspectorPhotoAppCompletionRequest = z.infer<
  typeof InspectorPhotoAppCompletionRequestSchema
>;

export const InspectorPhotoAppCompletionReceiptSchema = z
  .object({
    dispatchId: z.string().min(1),
    assignmentAttemptId: z.string().min(1),
    completionId: z.string().min(1),
    completedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type InspectorPhotoAppCompletionReceipt = z.infer<
  typeof InspectorPhotoAppCompletionReceiptSchema
>;

export const InspectorPhotoAppCompletionResponseSchema = z.object({
  receipt: InspectorPhotoAppCompletionReceiptSchema,
});
export type InspectorPhotoAppCompletionResponse = z.infer<
  typeof InspectorPhotoAppCompletionResponseSchema
>;

const InspectorPortalAssignmentFileEvidenceSchema = z.object({
  evidenceId: z.string().min(1),
  dispatchId: z.string().min(1),
  assignmentAttemptId: z.string().min(1),
  inspectorId: z.string().min(1),
  capturedAt: z.string().nullable(),
});

export const InspectorPortalAssignmentFileSchema = z.discriminatedUnion(
  "qualifiesForCompletion",
  [
    InspectorPortalUploadedFileSchema.merge(
      InspectorPortalAssignmentFileEvidenceSchema.extend({
        qualifiesForCompletion: z.literal(true),
      }),
    ),
    InspectorPortalUploadedFileSchema.extend({
      qualifiesForCompletion: z.literal(false),
      evidenceId: z.null(),
      dispatchId: z.null(),
      assignmentAttemptId: z.null(),
      inspectorId: z.null(),
      capturedAt: z.null(),
    }),
  ],
);
export type InspectorPortalAssignmentFile = z.infer<
  typeof InspectorPortalAssignmentFileSchema
>;

export const InspectorPortalAssignmentDetailSchema =
  InspectorPortalAssignmentSummarySchema.extend({
    property: z.object({
      latitude: z.number().nullable(),
      longitude: z.number().nullable(),
    }),
    files: z.array(InspectorPortalAssignmentFileSchema),
  });
export type InspectorPortalAssignmentDetail = z.infer<
  typeof InspectorPortalAssignmentDetailSchema
>;

export const InspectorPortalAssignmentDetailResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    assignment: InspectorPortalAssignmentDetailSchema,
  }),
});
export type InspectorPortalAssignmentDetailResponse = z.infer<
  typeof InspectorPortalAssignmentDetailResponseSchema
>;

export const InspectorPortalAssignmentAuthorityQuerySchema = z
  .object({
    attemptId: z.string().min(1),
  })
  .strict();
export type InspectorPortalAssignmentAuthorityQuery = z.infer<
  typeof InspectorPortalAssignmentAuthorityQuerySchema
>;

export const InspectorPortalAssignmentAuthorityOutcomeSchema =
  z.discriminatedUnion("kind", [
    z
      .object({
        kind: z.literal("active"),
        dispatchId: z.string().min(1),
        assignmentAttemptId: z.string().min(1),
        inspectorId: z.string().min(1),
        assignment: InspectorPortalAssignmentDetailSchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("completed"),
        dispatchId: z.string().min(1),
        assignmentAttemptId: z.string().min(1),
        completionId: z.string().min(1).nullable(),
        completedAt: z.string().datetime({ offset: true }).nullable(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("inactive"),
        dispatchId: z.string().min(1),
        assignmentAttemptId: z.string().min(1),
        reason: z.enum([
          "cancelled",
          "no_longer_assigned",
          "attempt_ended",
          "not_photo_app_eligible",
          "absent",
        ]),
      })
      .strict(),
  ]);
export type InspectorPortalAssignmentAuthorityOutcome = z.infer<
  typeof InspectorPortalAssignmentAuthorityOutcomeSchema
>;

export const InspectorPortalAssignmentAuthorityResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    outcome: InspectorPortalAssignmentAuthorityOutcomeSchema,
  }),
});
export type InspectorPortalAssignmentAuthorityResponse = z.infer<
  typeof InspectorPortalAssignmentAuthorityResponseSchema
>;

export const InspectorPortalAssignmentOfferSchema = z
  .object({
    dispatchId: z.string().min(1),
    assignmentAttemptId: z.string().min(1),
    version: z.number().int().positive(),
    evalionId: z.string().min(1),
    address: z.string(),
    viewingDeadline: z.string().nullable(),
    product: z.string().min(1),
    inspection: z.enum(["both", "exterior"]),
    estimatedTravelKm: z.number().nonnegative().nullable(),
    inspectorInstructions: z.string().nullable(),
  })
  .strict();
export type InspectorPortalAssignmentOffer = z.infer<
  typeof InspectorPortalAssignmentOfferSchema
>;

export const InspectorPortalAssignmentOfferOutcomeSchema = z.discriminatedUnion(
  "kind",
  [
    z
      .object({
        kind: z.literal("actionable"),
        offer: InspectorPortalAssignmentOfferSchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("accepted"),
        dispatchId: z.string().min(1),
        assignmentAttemptId: z.string().min(1),
      })
      .strict(),
    z
      .object({
        kind: z.literal("declined"),
        dispatchId: z.string().min(1),
        assignmentAttemptId: z.string().min(1),
      })
      .strict(),
    z.object({ kind: z.literal("unavailable") }).strict(),
  ],
);
export type InspectorPortalAssignmentOfferOutcome = z.infer<
  typeof InspectorPortalAssignmentOfferOutcomeSchema
>;

export const InspectorPortalAssignmentOfferResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({ outcome: InspectorPortalAssignmentOfferOutcomeSchema }),
});
export type InspectorPortalAssignmentOfferResponse = z.infer<
  typeof InspectorPortalAssignmentOfferResponseSchema
>;

export const InspectorPortalAssignmentActionSchema = z.object({
  assignmentAttemptId: z.string().min(1).optional(),
  version: z.number().int().positive().optional(),
  scheduledFor: z.string().min(1).optional(),
  declineReason: z.string().min(1).optional(),
});
export type InspectorPortalAssignmentAction = z.infer<
  typeof InspectorPortalAssignmentActionSchema
>;

export const InspectorPortalAssignmentActionResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    assignment: InspectorPortalAssignmentSummarySchema,
  }),
});
export type InspectorPortalAssignmentActionResponse = z.infer<
  typeof InspectorPortalAssignmentActionResponseSchema
>;

export const InspectorPortalAssignmentUploadResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    files: z.array(InspectorPortalUploadedFileSchema),
  }),
});
export type InspectorPortalAssignmentUploadResponse = z.infer<
  typeof InspectorPortalAssignmentUploadResponseSchema
>;

export const InspectorPortalAuthLogoutResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    loggedOut: z.literal(true),
  }),
});
export type InspectorPortalAuthLogoutResponse = z.infer<
  typeof InspectorPortalAuthLogoutResponseSchema
>;

export const InspectorAvailabilityStatusSchema = z.enum([
  "active",
  "inactive",
  "vacation",
]);
export type InspectorAvailabilityStatus = z.infer<
  typeof InspectorAvailabilityStatusSchema
>;

export const InspectorCapabilitySchema = z.enum([
  "Interior",
  "Exterior",
  "digitalInspection",
  "exteriorOnSiteInspection",
  "interiorExteriorOnSiteInspection",
]);
export type InspectorCapability = z.infer<typeof InspectorCapabilitySchema>;

export const InspectorPaymentMethodSchema = z.enum(["Invoice", "Freelance"]);
export type InspectorPaymentMethod = z.infer<
  typeof InspectorPaymentMethodSchema
>;

export const InspectorReviewSchema = z.object({
  id: z.string().min(1),
  rating: z.number().min(1).max(5),
  note: z.string().min(1),
  reviewerName: z.string().min(1),
  createdAt: z.string().min(1),
});
export type InspectorReview = z.infer<typeof InspectorReviewSchema>;

export const InspectorAssignmentHistorySchema = z.object({
  id: z.string().min(1),
  evalionId: z.string().min(1),
  addressLine: z.string().min(1),
  productType: z.string().min(1),
  completedAt: z.string().min(1),
  outcome: z.string().min(1),
});
export type InspectorAssignmentHistory = z.infer<
  typeof InspectorAssignmentHistorySchema
>;

export const InspectorRegistryRecordSchema = z.object({
  id: z.string().min(1),
  fullName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1),
  homeBaseLocation: z.string().min(1),
  serviceRadiusKm: z.number().positive(),
  capability: InspectorCapabilitySchema,
  inspectorType: z.string().min(1),
  productTypes: z.array(z.string().min(1)),
  paymentMethod: InspectorPaymentMethodSchema,
  availabilityStatus: InspectorAvailabilityStatusSchema,
  vacationEndDate: z.string().nullable(),
  internalNotes: z.string(),
  invitedAt: z.string().nullable(),
  reviews: z.array(InspectorReviewSchema),
  assignmentHistory: z.array(InspectorAssignmentHistorySchema),
});
export type InspectorRegistryRecord = z.infer<
  typeof InspectorRegistryRecordSchema
>;

export const ViewingsDispatchInspectorRegistryRecordSchema = z.object({
  id: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  fullName: z.string().min(1),
  email: z.string().email().nullable(),
  phone: z.string().min(1),
  homeBaseLabel: z.string().min(1),
  homeBaseLatitude: z.number(),
  homeBaseLongitude: z.number(),
  serviceRadiusKm: z.number().positive(),
  capabilities: z.array(InspectorCapabilitySchema).min(1),
  inspectorType: z.array(z.string().min(1)),
  productTypes: z.array(z.string().min(1)),
  availabilityStatus: InspectorAvailabilityStatusSchema,
  portalAccessStatus: z.enum(["invited", "active", "disabled", "blocked"]),
  vacationStartDate: z.string().nullable(),
  vacationEndDate: z.string().nullable(),
  invitedAt: z.string().nullable(),
});
export type ViewingsDispatchInspectorRegistryRecord = z.infer<
  typeof ViewingsDispatchInspectorRegistryRecordSchema
>;

export const ViewingsDispatchInspectorCandidateSchema = z.object({
  id: z.string().min(1),
  rank: z.number().int().positive().optional(),
  fullName: z.string().min(1),
  phone: z.string().min(1),
  homeBaseLabel: z.string().min(1),
  capabilities: z.array(InspectorCapabilitySchema).min(1),
  productTypes: z.array(z.string().min(1)),
  availabilityStatus: InspectorAvailabilityStatusSchema,
  distanceKm: z.number().nonnegative().nullable(),
  isWithinRadius: z.boolean().optional(),
  matchReasons: z.array(ViewingsDispatchMatchReasonSchema).optional(),
  isAssigned: z.boolean(),
  isSuggested: z.boolean(),
});
export type ViewingsDispatchInspectorCandidate = z.infer<
  typeof ViewingsDispatchInspectorCandidateSchema
>;

export const ViewingsDispatchInspectorCandidatesResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspectors: z.array(ViewingsDispatchInspectorCandidateSchema),
    manualInspectors: z
      .array(ViewingsDispatchInspectorCandidateSchema)
      .optional(),
    reason: z.literal("match_run_required").optional(),
    noMatchSummary: ViewingsDispatchNoMatchSummarySchema.optional(),
  }),
});
export type ViewingsDispatchInspectorCandidatesResponse = z.infer<
  typeof ViewingsDispatchInspectorCandidatesResponseSchema
>;

export const ViewingsDispatchInspectorRegistryQuerySchema = z.object({
  search: z.string().optional(),
  availabilityStatus: z
    .string()
    .refine((value) =>
      value
        .split(",")
        .every(
          (status) =>
            InspectorAvailabilityStatusSchema.safeParse(status).success,
        ),
    )
    .optional(),
  region: z.string().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type ViewingsDispatchInspectorRegistryQuery = z.infer<
  typeof ViewingsDispatchInspectorRegistryQuerySchema
>;

export const ViewingsDispatchInspectorRegistryResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    paginationInfo: PaginationInfoSchema,
    filterOptions: z.object({
      availabilityStatuses: z.array(InspectorAvailabilityStatusSchema),
      regions: z.array(z.string().min(1)),
    }),
    inspectors: z.array(ViewingsDispatchInspectorRegistryRecordSchema),
  }),
});
export type ViewingsDispatchInspectorRegistryResponse = z.infer<
  typeof ViewingsDispatchInspectorRegistryResponseSchema
>;

export const ViewingsDispatchInspectorCreateRequestSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email().nullable(),
  phone: z.string().min(1),
  homeBaseLabel: z.string().min(1),
  homeBaseLatitude: z.number(),
  homeBaseLongitude: z.number(),
  serviceRadiusKm: z.coerce.number().int().positive(),
  capabilities: z.array(InspectorCapabilitySchema).min(1),
  productTypes: z.array(z.string().min(1)),
  availabilityStatus: InspectorAvailabilityStatusSchema.default("active"),
  vacationStartDate: z.string().nullable().optional(),
  vacationEndDate: z.string().nullable().optional(),
});
export type ViewingsDispatchInspectorCreateRequest = z.infer<
  typeof ViewingsDispatchInspectorCreateRequestSchema
>;

export const ViewingsDispatchInspectorUpdateRequestSchema =
  ViewingsDispatchInspectorCreateRequestSchema.partial();
export type ViewingsDispatchInspectorUpdateRequest = z.infer<
  typeof ViewingsDispatchInspectorUpdateRequestSchema
>;

export const ViewingsDispatchInspectorRecordResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspector: ViewingsDispatchInspectorRegistryRecordSchema,
  }),
});
export type ViewingsDispatchInspectorRecordResponse = z.infer<
  typeof ViewingsDispatchInspectorRecordResponseSchema
>;

export const ViewingsDispatchInspectorInviteResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspectorId: z.string().min(1),
    portalAccessStatus: z.literal("invited"),
    invitedAt: z.string().min(1),
  }),
});
export type ViewingsDispatchInspectorInviteResponse = z.infer<
  typeof ViewingsDispatchInspectorInviteResponseSchema
>;

export const ViewingsDispatchInspectorConfirmRequestSchema = z
  .object({
    inspectorId: z.string().min(1),
    version: z.number().int().positive(),
    sourceType: ViewingsDispatchSourceTypeSchema.optional(),
  })
  .strict();
export type ViewingsDispatchInspectorConfirmRequest = z.infer<
  typeof ViewingsDispatchInspectorConfirmRequestSchema
>;

export const ViewingsDispatchInspectorConfirmResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    dispatch: ViewingsDispatchSummarySchema,
  }),
});
export type ViewingsDispatchInspectorConfirmResponse = z.infer<
  typeof ViewingsDispatchInspectorConfirmResponseSchema
>;

export const ViewingsDispatchOfferSendRequestSchema = z.object({
  inspectorId: z.string().min(1),
  message: z.string().min(1).optional(),
  inspectorInstructions: z.string().min(1).optional(),
  sourceType: ViewingsDispatchSourceTypeSchema.optional(),
});
export type ViewingsDispatchOfferSendRequest = z.infer<
  typeof ViewingsDispatchOfferSendRequestSchema
>;

export const ViewingsDispatchOfferSendResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    dispatch: ViewingsDispatchSummarySchema,
    attemptId: z.string().min(1),
    communicationId: z.string().nullable(),
  }),
});
export type ViewingsDispatchOfferSendResponse = z.infer<
  typeof ViewingsDispatchOfferSendResponseSchema
>;

export const InspectorRegistryResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspectors: z.array(InspectorRegistryRecordSchema),
  }),
});
export type InspectorRegistryResponse = z.infer<
  typeof InspectorRegistryResponseSchema
>;

export const InspectorRegistryQuerySchema = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  sortBy: z
    .enum([
      "fullName",
      "phone",
      "homeBaseLocation",
      "capability",
      "reviews",
      "availabilityStatus",
      "paymentMethod",
    ])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});
export type InspectorRegistryQuery = z.infer<
  typeof InspectorRegistryQuerySchema
>;

export const InspectorRegistryRecordResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspector: InspectorRegistryRecordSchema,
  }),
});
export type InspectorRegistryRecordResponse = z.infer<
  typeof InspectorRegistryRecordResponseSchema
>;

export const UpsertInspectorRegistryRecordSchema =
  InspectorRegistryRecordSchema;
export type UpsertInspectorRegistryRecord = z.infer<
  typeof UpsertInspectorRegistryRecordSchema
>;

export const AddInspectorReviewSchema = z.object({
  inspectorId: z.string().min(1),
  rating: z.number().min(1).max(5),
  note: z.string().min(1),
});
export type AddInspectorReview = z.infer<typeof AddInspectorReviewSchema>;
